import { differenceInDays } from 'date-fns'

/**
 * Calculate the position of the Today marker within a timeline
 * @param minDate - The start date of the timeline range
 * @param maxDate - The end date of the timeline range
 * @param containerWidth - The width of the timeline container in pixels
 * @returns The position in pixels from the left, or null if today is outside the range
 */
export const calculateTodayPosition = (
  minDate: Date,
  maxDate: Date,
  containerWidth: number
): number | null => {
  const today = new Date()

  // Normalize dates to start of day to avoid millisecond precision issues
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  const minDateStart = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate())
  const maxDateStart = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate())

  // Only calculate position if today falls within the timeline range
  if (todayStart < minDateStart || todayStart > maxDateStart) {
    return null
  }

  const totalTimespan = differenceInDays(maxDateStart, minDateStart)

  // Handle edge case where timeline has zero duration
  if (totalTimespan <= 0) {
    return null
  }

  // Calculate today's position with mathematical precision
  const todayOffset = differenceInDays(todayStart, minDateStart)
  const todayPosition = (todayOffset / totalTimespan) * containerWidth

  return todayPosition
}

/**
 * Check if today's date falls within the given timeline range
 * @param minDate - The start date of the timeline range
 * @param maxDate - The end date of the timeline range
 * @returns True if today is within the range, false otherwise
 */
export const isTodayInRange = (minDate: Date, maxDate: Date): boolean => {
  const today = new Date()

  // Normalize dates to start of day to avoid millisecond precision issues
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  const minDateStart = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate())
  const maxDateStart = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate())

  return todayStart >= minDateStart && todayStart <= maxDateStart
}

/**
 * Calculate the optimal scroll position to center the Today marker in the timeline viewport
 * @param minDate - The start date of the timeline range
 * @param maxDate - The end date of the timeline range
 * @param containerWidth - The width of the timeline container in pixels
 * @param viewportWidth - The width of the visible viewport in pixels
 * @param contextRatio - The ratio of context to show (0.3 = 30% past, 70% future)
 * @returns The optimal scroll position in pixels, or null if today is outside the range
 */
export const calculateOptimalScrollPosition = (
  minDate: Date,
  maxDate: Date,
  containerWidth: number,
  viewportWidth: number,
  contextRatio: number = 0.3
): number | null => {
  // First check if today is in range
  if (!isTodayInRange(minDate, maxDate)) {
    return null
  }

  // Calculate today's position
  const todayPosition = calculateTodayPosition(minDate, maxDate, containerWidth)
  if (todayPosition === null) {
    return null
  }

  // Calculate the desired position within the viewport
  // contextRatio = 0.3 means show 30% past timeline, 70% future timeline
  const desiredPositionInViewport = viewportWidth * contextRatio

  // Calculate the scroll position needed to achieve this
  const scrollPosition = todayPosition - desiredPositionInViewport

  // Ensure scroll position is within valid bounds
  const maxScrollPosition = Math.max(0, containerWidth - viewportWidth)
  const clampedScrollPosition = Math.max(0, Math.min(scrollPosition, maxScrollPosition))

  return clampedScrollPosition
}

/**
 * Get Today marker styling based on theme mode
 * @param isDarkMode - Whether the app is in dark mode
 * @returns Styling object for the Today marker
 */
export const getTodayMarkerStyles = (isDarkMode: boolean) => ({
  header: {
    backgroundColor: isDarkMode ? 'rgba(158, 158, 158, 0.6)' : 'rgba(117, 117, 117, 0.6)',
    labelColor: isDarkMode ? 'rgba(158, 158, 158, 0.8)' : 'rgba(117, 117, 117, 0.8)',
    labelBackground: isDarkMode ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.9)',
    labelBorder: isDarkMode ? 'rgba(158, 158, 158, 0.3)' : 'rgba(117, 117, 117, 0.3)'
  },
  rows: {
    backgroundColor: isDarkMode ? 'rgba(158, 158, 158, 0.4)' : 'rgba(117, 117, 117, 0.4)'
  }
})
